import React, { useEffect, useRef, useState } from "react";
import { Plus, Shield, User, Users, Trash2, CheckCircle, Clock, CreditCard, Save, ChevronDown, ChevronUp, Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DhanTokenManager from "./DhanTokenManager";

const TOKEN_KEY = "ct_login_token";
const USER_KEY = "ct_login_user";

const BLOOMBERG_MANUAL_SUPPORT_TABS = [
  { id: "nifty-ce", label: "NIFTY CE", chainKey: "nifty", side: "CE" },
  { id: "nifty-pe", label: "NIFTY PE", chainKey: "nifty", side: "PE" },
  { id: "banknifty-ce", label: "BN CE", chainKey: "banknifty", side: "CE" },
  { id: "banknifty-pe", label: "BN PE", chainKey: "banknifty", side: "PE" },
  { id: "sensex-ce", label: "SENSEX CE", chainKey: "sensex", side: "CE" },
  { id: "sensex-pe", label: "SENSEX PE", chainKey: "sensex", side: "PE" },
  { id: "bankex-ce", label: "BANKEX CE", chainKey: "bankex", side: "CE" },
  { id: "bankex-pe", label: "BANKEX PE", chainKey: "bankex", side: "PE" },
];

const manualSupportKey = (chainKey, side, strike) =>
  `${chainKey}-${side}-${Number(strike || 0)}`;

const MANUAL_SUPPORT_FETCH_STARTED_KEY = "bloomberg_manual_support_fetch_started_v1";
const MANUAL_SUPPORT_AUTO_REFRESH_KEY = "bloomberg_manual_support_auto_refresh_v1";
const MANUAL_SUPPORT_ROWS_KEY = "bloomberg_manual_support_rows_v1";
const MANUAL_SUPPORT_FETCHED_ONCE_KEY = "bloomberg_manual_support_fetched_once_v1";

function readManualSupportRowsCache() {
  try {
    return JSON.parse(localStorage.getItem(MANUAL_SUPPORT_ROWS_KEY) || "{}");
  } catch {
    return {};
  }
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", role: "user", fullName: "", email: "", phone: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [userEdits, setUserEdits] = useState({});

  const [adminInnerTab, setAdminInnerTab] = useState("dashboard");
  const [showSecretSettings, setShowSecretSettings] = useState(false);
  const [showRoleAccess, setShowRoleAccess] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showLoginAccounts, setShowLoginAccounts] = useState(false);
  const [showSecretRefresh, setShowSecretRefresh] = useState(false);
  const [showSupportRefresh, setShowSupportRefresh] = useState(false);
  const [adminDropdownResetKey, setAdminDropdownResetKey] = useState(0);
  const [showUserSessions, setShowUserSessions] = useState(false);
  const [sessionUsers, setSessionUsers] = useState([]);
  const [sessionSearchText, setSessionSearchText] = useState("");
  const [faceLoginUsers, setFaceLoginUsers] = useState([]);
  const [showFaceLoginSettings, setShowFaceLoginSettings] = useState(false);

  const [manualSupportTab, setManualSupportTab] = useState(BLOOMBERG_MANUAL_SUPPORT_TABS[0]);
  const [manualSupportSearch, setManualSupportSearch] = useState("");
  const [manualSupportRows, setManualSupportRows] = useState([]);
  const [manualSupportLoading, setManualSupportLoading] = useState(false);
  const [manualSupportFetchStarted, setManualSupportFetchStarted] = useState(
  () => localStorage.getItem(MANUAL_SUPPORT_FETCH_STARTED_KEY) === "1"
  );

  const [manualSupportFetchedOnce, setManualSupportFetchedOnce] = useState(
    () => localStorage.getItem(MANUAL_SUPPORT_FETCHED_ONCE_KEY) === "1"
  );
  const [manualSupportAutoRefresh, setManualSupportAutoRefresh] = useState(
    () => localStorage.getItem(MANUAL_SUPPORT_AUTO_REFRESH_KEY) === "1"
  );
  const manualTopScrollRef = useRef(null);
  const manualTableScrollRef = useRef(null);

  const syncManualSupportScroll = source => {
    const top = manualTopScrollRef.current;
    const table = manualTableScrollRef.current;

    if (!top || !table) return;

    if (source === "top") {
      table.scrollLeft = top.scrollLeft;
    } else {
      top.scrollLeft = table.scrollLeft;
    }
  };
  const [manualSupportSort, setManualSupportSort] = useState({
    key: "entry",
    direction: "desc",
  });

  const [secretSettings, setSecretSettings] = useState({
    clientId: "",
    accessToken: "",
    refreshSeconds: 10,
    optionDecayPremiumMin: 5,
    optionDecayPremiumMax: 150,
    optionDecayNiftyPremiumMin: 5,
    optionDecayNiftyPremiumMax: 150,
    optionDecaySensexPremiumMin: 5,
    optionDecaySensexPremiumMax: 150,
    optionDecayBankNiftyPremiumMin: 5,
    optionDecayBankNiftyPremiumMax: 150,
    secretOptionDecayNiftyPremiumMin: 5,
    secretOptionDecayNiftyPremiumMax: 150,
    secretOptionDecaySensexPremiumMin: 5,
    secretOptionDecaySensexPremiumMax: 150,
    secretOptionDecayBankNiftyPremiumMin: 5,
    secretOptionDecayBankNiftyPremiumMax: 150,
    supportRefreshSeconds: 30,
    bloombergRefreshSeconds: 30,
    bloombergScannerNiftyQty: 50,
    bloombergScannerNiftySlPoints: 15,
    bloombergScannerNiftyCandleMinutes: 1,

    bloombergScannerBankNiftyQty: 50,
    bloombergScannerBankNiftySlPoints: 15,
    bloombergScannerBankNiftyCandleMinutes: 1,

    bloombergScannerSensexQty: 50,
    bloombergScannerSensexSlPoints: 15,
    bloombergScannerSensexCandleMinutes: 1,

    bloombergScannerBankexQty: 50,
    bloombergScannerBankexSlPoints: 15,
    bloombergScannerBankexCandleMinutes: 1,
    bloombergManualSupportRanges: {},
  });

  const [rolePageAccess, setRolePageAccess] = useState({
    admin: [],
    user: [],
    secret: [],
    nexo: [],
  });

  const [paymentDetails, setPaymentDetails] = useState({
  upiId: "",
  qrImageUrl: "",
  accountName: "",
  bankName: "",
  accountNumber: "",
  ifsc: "",
  instructions: "",
});

  const token = localStorage.getItem(TOKEN_KEY);
  const currentUser = JSON.parse(localStorage.getItem(USER_KEY) || "null");

  const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

const PAGE_OPTIONS = [
  { path: "/", label: "Dashboard" },
  { path: "/admin-dashboard", label: "Admin Dashboard" },
  { path: "/x-portal", label: "Secret Entry" },
  { path: "/x-admin", label: "Secret Page" },
  { path: "/nifty-support", label: "Support Scanner" },
  { path: "/bloomberg-terminal", label: "Bloomberg Terminal" },
  { path: "/option-decay", label: "Option Decay Desk" },
  { path: "/expiry-decay-desk", label: "Expiry Decay Desk" },
  { path: "/market-reaction", label: "Market Reaction" },
  { path: "/strike-market-reaction", label: "Strike Market Reaction" },
  { path: "/stocks", label: "Stocks" },
  { path: "/dhan-stocks", label: "Dhan Stocks" },
  { path: "/dhan-big-money-flow", label: "Dhan Big Money Flow" },
  { path: "/indicators", label: "Indicators" },
  { path: "/world-monitor", label: "World Monitor" },
  { path: "/accounts", label: "Accounts" },
  { path: "/groups", label: "Groups" },
  { path: "/orders", label: "Orders" },
  { path: "/Positions", label: "Positions" },
  { path: "/bloomberg-watch", label: "BloombergWatch" },
  { path: "/copy-trading", label: "Copy Trading" },
  { path: "/nexo", label: "Nexo" },
  { path: "/nexo-assistant", label: "Nexo Assistant" },
  { path: "/banknifty", label: "BankNifty" },
  { path: "/bankex", label: "Bankex" },
  { path: "/jammu", label: "Jammu" },
];

const ROLE_OPTIONS = ["admin", "user", "secret", "nexo"];


  //Login modify
  const logoutToLogin = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem("ct_admin_auth");
  navigate("/login", { replace: true });
};

const authHeaders = {
  "Content-Type": "application/json",
  "Authorization": `Bearer ${token}`,
};

//Save Setting functions

const saveSecretExpiryDeskRefreshSettings = async () => {
  setMessage("");
  setError("");

  const res = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify({
      ...secretSettings,
      secretExpiryDeskRefreshSeconds: Number(secretSettings.secretExpiryDeskRefreshSeconds || 5),
    }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.success) {
    setError(data.error || "Expiry Decay Desk refresh save failed");
    return;
  }

  setSecretSettings(data.settings || secretSettings);
  setMessage("Expiry Decay Desk refresh saved");
  setAdminDropdownResetKey(prev => prev + 1);
};

const saveSecretRefreshSettings = async () => {
    setMessage("");
    setError("");

    const res = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify({
        ...secretSettings,
        refreshSeconds: Number(secretSettings.refreshSeconds || 10),
        supportRefreshSeconds: Number(secretSettings.supportRefreshSeconds || 30),
        bloombergRefreshSeconds: Math.max(1, Number(secretSettings.bloombergRefreshSeconds || 1)),
      }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      setError(data.error || "Refresh settings save failed");
      return;
    }

    setSecretSettings(data.settings || secretSettings);
    setMessage("Secret page refresh time saved");
    setAdminDropdownResetKey(prev => prev + 1);
    setShowSecretRefresh(false);
    setShowSupportRefresh(false);
  };

 const saveExpiryDecaySettings = async () => {
  setMessage("");
  setError("");

  const res = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify({
      ...secretSettings,
      optionDecayPremiumMin: Number(secretSettings.optionDecayPremiumMin || 5),
      optionDecayPremiumMax: Number(secretSettings.optionDecayPremiumMax || 150),
    }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.success) {
    setError(data.error || "Expiry Decay settings save failed");
    return;
  }

  setSecretSettings(data.settings || secretSettings);
  setMessage("Expiry Decay Desk settings saved");
  setAdminDropdownResetKey(prev => prev + 1);
}; 

const saveSecretPageExpiryDecaySettings = async () => {
  setMessage("");
  setError("");

  const res = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(secretSettings),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.success) {
    setError(data.error || "Secret Page Expiry Decay settings save failed");
    return;
  }

  setSecretSettings(data.settings || secretSettings);
  setMessage("Secret Page Expiry Decay settings saved");
  setAdminDropdownResetKey(prev => prev + 1);
};

const loadManualSupportRows = async () => {
  setManualSupportLoading(true);
  setError("");

  try {
    const res = await fetch(`${API_BASE}/api/bloomberg/snapshot?t=${Date.now()}`, {
      cache: "no-store",
    });
    const data = await res.json().catch(() => ({}));

    const chain = data.optionChains?.[manualSupportTab.chainKey];
    const rows = manualSupportTab.side === "CE" ? chain?.calls || [] : chain?.puts || [];

    const nextRows = rows
      .filter(row => Number(row.ltp || 0) >= 5 && Number(row.ltp || 0) <= 600)
      .sort((a, b) => Number(b.ltp || 0) - Number(a.ltp || 0));

    setManualSupportRows(nextRows);

    const cachedRows = readManualSupportRowsCache();
    localStorage.setItem(
      MANUAL_SUPPORT_ROWS_KEY,
      JSON.stringify({
        ...cachedRows,
        [manualSupportTab.id]: nextRows,
      })
    );

    localStorage.setItem(MANUAL_SUPPORT_FETCHED_ONCE_KEY, "1");
    setManualSupportFetchedOnce(true);
  } catch (err) {
    setError(err.message || "Manual support rows load failed");
  } finally {
    setManualSupportLoading(false);
  }
};

const startManualSupportFetch = () => {
  localStorage.setItem(MANUAL_SUPPORT_FETCH_STARTED_KEY, "1");
  localStorage.setItem(MANUAL_SUPPORT_AUTO_REFRESH_KEY, "1");

  setManualSupportFetchStarted(true);
  setManualSupportAutoRefresh(true);

  window.dispatchEvent(new Event("bloomberg-manual-support-fetch-control"));
};

const stopManualSupportFetch = () => {
  localStorage.setItem(MANUAL_SUPPORT_FETCH_STARTED_KEY, "0");

  setManualSupportFetchStarted(false);

  window.dispatchEvent(new Event("bloomberg-manual-support-fetch-control"));
};

useEffect(() => {
  const applyRows = event => {
    const rowsByTab = event.detail?.rowsByTab || readManualSupportRowsCache();
    setManualSupportRows(rowsByTab[manualSupportTab.id] || []);
  };

  window.addEventListener("bloomberg-manual-support-rows", applyRows);

  const cachedRows = readManualSupportRowsCache();
  setManualSupportRows(cachedRows[manualSupportTab.id] || []);

  return () => {
    window.removeEventListener("bloomberg-manual-support-rows", applyRows);
  };
}, [manualSupportTab.id]);

const getManualSupportDefaults = chainKey => {
  if (chainKey === "banknifty") {
    return {
      quantity: secretSettings.bloombergScannerBankNiftyQty || 50,
      slPoints: secretSettings.bloombergScannerBankNiftySlPoints || 15,
      candleMinutes: secretSettings.bloombergScannerBankNiftyCandleMinutes || 1,
    };
  }

  if (chainKey === "sensex") {
    return {
      quantity: secretSettings.bloombergScannerSensexQty || 50,
      slPoints: secretSettings.bloombergScannerSensexSlPoints || 15,
      candleMinutes: secretSettings.bloombergScannerSensexCandleMinutes || 1,
    };
  }

  if (chainKey === "bankex") {
    return {
      quantity: secretSettings.bloombergScannerBankexQty || 50,
      slPoints: secretSettings.bloombergScannerBankexSlPoints || 15,
      candleMinutes: secretSettings.bloombergScannerBankexCandleMinutes || 1,
    };
  }

  return {
    quantity: secretSettings.bloombergScannerNiftyQty || 50,
    slPoints: secretSettings.bloombergScannerNiftySlPoints || 15,
    candleMinutes: secretSettings.bloombergScannerNiftyCandleMinutes || 1,
  };
};

const getManualStopLossPrice = (row, saved, defaults) => {
  if (saved.stopLossPrice !== undefined && saved.stopLossPrice !== "") {
    return saved.stopLossPrice;
  }

  const supportLow = Number(saved.supportLow || 0);
  const slPoints = Math.max(0, Number(saved.slPoints || defaults.slPoints || 0));

  if (!supportLow) return "";

  return Number(Math.max(0.05, supportLow - slPoints).toFixed(2));
};

const getManualTotalSlPoints = (row, stopLossPrice) => {
  const entry = Number(row.ltp || 0);
  const stopLoss = Number(stopLossPrice || 0);

  if (!entry || !stopLoss) return 0;

  return Number(Math.max(0, entry - stopLoss).toFixed(2));
};

const updateManualSupportRange = (row, field, value) => {
  const key = manualSupportKey(manualSupportTab.chainKey, manualSupportTab.side, row.strike);

  setSecretSettings(prev => {
    const current = prev.bloombergManualSupportRanges?.[key] || {};
    const defaults = getManualSupportDefaults(manualSupportTab.chainKey);

    const nextRow = {
      ...current,
      chainKey: manualSupportTab.chainKey,
      side: manualSupportTab.side,
      strike: Number(row.strike || 0),
      entry: Number(row.ltp || 0),
      supportLow: current.supportLow || "",
      supportHigh: current.supportHigh || "",
      quantity: current.quantity || defaults.quantity,
      slPoints: current.slPoints || defaults.slPoints,
      candleMinutes: current.candleMinutes || defaults.candleMinutes,
      notifyEnabled: current.notifyEnabled === true,
      [field]: value,
    };

    if (field === "stopLossPrice") {
      nextRow.stopLossManual = value !== "";
    }

    if ((field === "supportLow" || field === "slPoints") && !nextRow.stopLossManual) {
      const supportLow = Number(nextRow.supportLow || 0);
      const slPoints = Math.max(0, Number(nextRow.slPoints || 0));

      nextRow.stopLossPrice =
        supportLow
          ? Number(Math.max(0.05, supportLow - slPoints).toFixed(2))
          : "";
    }

    return {
      ...prev,
      bloombergManualSupportRanges: {
        ...(prev.bloombergManualSupportRanges || {}),
        [key]: nextRow,
      },
    };
  });
};

const toggleManualSupportSort = key => {
  setManualSupportSort(prev => {
    if (prev.key === key) {
      return {
        key,
        direction: prev.direction === "desc" ? "asc" : "desc",
      };
    }

    return { key, direction: "desc" };
  });
};

const sortedManualSupportRows = [...manualSupportRows]
  .filter(row => String(row.strike || "").includes(manualSupportSearch.trim()))
  .sort((a, b) => {
    const aValue = manualSupportSort.key === "strike"
      ? Number(a.strike || 0)
      : Number(a.ltp || 0);

    const bValue = manualSupportSort.key === "strike"
      ? Number(b.strike || 0)
      : Number(b.ltp || 0);

    return manualSupportSort.direction === "asc"
      ? aValue - bValue
      : bValue - aValue;
  });

  const saveBloombergSettings = async () => {
  setMessage("");
  setError("");

  const res = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify({
      ...secretSettings,

      bloombergScannerNiftyQty: Number(secretSettings.bloombergScannerNiftyQty || 50),
      bloombergScannerNiftySlPoints: Number(secretSettings.bloombergScannerNiftySlPoints || 15),
      bloombergScannerNiftyCandleMinutes: Number(secretSettings.bloombergScannerNiftyCandleMinutes || 1),

      bloombergScannerBankNiftyQty: Number(secretSettings.bloombergScannerBankNiftyQty || 50),
      bloombergScannerBankNiftySlPoints: Number(secretSettings.bloombergScannerBankNiftySlPoints || 15),
      bloombergScannerBankNiftyCandleMinutes: Number(secretSettings.bloombergScannerBankNiftyCandleMinutes || 1),

      bloombergScannerSensexQty: Number(secretSettings.bloombergScannerSensexQty || 50),
      bloombergScannerSensexSlPoints: Number(secretSettings.bloombergScannerSensexSlPoints || 15),
      bloombergScannerSensexCandleMinutes: Number(secretSettings.bloombergScannerSensexCandleMinutes || 1),

      bloombergScannerBankexQty: Number(secretSettings.bloombergScannerBankexQty || 50),
      bloombergScannerBankexSlPoints: Number(secretSettings.bloombergScannerBankexSlPoints || 15),
      bloombergScannerBankexCandleMinutes: Number(secretSettings.bloombergScannerBankexCandleMinutes || 1),
    }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.success) {
    setError(data.error || "Bloomberg settings save failed");
    return;
  }

  setSecretSettings(data.settings || secretSettings);
  setMessage("Bloomberg settings saved");
  setAdminDropdownResetKey(prev => prev + 1);
  setAdminInnerTab("dashboard");
};

  const isFirstAdmin = (item) => {
    return String(item.id) === "admin" || String(item.username).toLowerCase() === "admin";
  };

  const filteredSessionUsers = sessionUsers.filter(item => {
    const q = sessionSearchText.toLowerCase().trim();

    if (!q) return true;

    return (
      String(item.username || "").toLowerCase().includes(q) ||
      String(item.fullName || "").toLowerCase().includes(q) ||
      String(item.role || "").toLowerCase().includes(q) ||
      String(item.id || "").toLowerCase().includes(q)
    );
  });

  const cleanDisplayIp = ip => {
    const cleanIp = String(ip || "").trim().replace(/^::ffff:/, "");
    if (cleanIp === "::1") return "127.0.0.1";
    return cleanIp || "Unknown";
  };

  const filteredUsers = users.filter(item => {
  const q = searchText.toLowerCase().trim();

  if (!q) return true;

  return (
    String(item.username || "").toLowerCase().includes(q) ||
    String(item.fullName || "").toLowerCase().includes(q) ||
    String(item.phone || "").toLowerCase().includes(q) ||
    String(item.email || "").toLowerCase().includes(q)
  );
});

      //Subscription Add on
  const loadPaymentDetails = async () => {
  const res = await fetch(`${API_BASE}/api/subscription/payment-details`, {
    headers: authHeaders,
  });

  const data = await res.json().catch(() => ({}));

  if (res.ok && data.success) {
    setPaymentDetails(data.paymentDetails || {});
  }
};

const savePaymentDetails = async () => {
  const res = await fetch(`${API_BASE}/api/subscription/payment-details`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(paymentDetails),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.success) {
    setError(data.error || "Payment details save failed");
    return;
  }

  setMessage("Payment details saved");
  setShowPaymentDetails(false);
};

const loadSecretSettings = async () => {
  const res = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
    headers: authHeaders,
  });

  const data = await res.json().catch(() => ({}));

  if (res.ok && data.success) {
    setSecretSettings(data.settings || {});
  }
};

const saveSecretSettings = async () => {
  setMessage("");
  setError("");

  const res = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(secretSettings),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.success) {
    setError(data.error || "Secret settings save failed");
    return;
  }

  setSecretSettings(data.settings || secretSettings);
  setMessage("Secret page credentials saved");
  setShowSecretSettings(false);
};

const loadRolePageAccess = async () => {
  const res = await fetch(`${API_BASE}/api/login/admin/role-page-access`, {
    headers: authHeaders,
  });

  const data = await res.json().catch(() => ({}));

  if (res.ok && data.success) {
    setRolePageAccess(data.rolePageAccess || {});
  }
};

const saveRolePageAccess = async () => {
  setMessage("");
  setError("");

  const res = await fetch(`${API_BASE}/api/login/admin/role-page-access`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify({ rolePageAccess }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.success) {
    setError(data.error || "Role page access save failed");
    return;
  }

  setRolePageAccess(data.rolePageAccess || rolePageAccess);
  setMessage("Role page access saved");
  setShowRoleAccess(false);
};

const loadUserSessions = async () => {
  const res = await fetch(`${API_BASE}/api/login/admin/user-sessions`, {
    headers: authHeaders,
  });

  const data = await res.json().catch(() => ({}));
  if (res.ok && data.success) {
    setSessionUsers(data.users || []);
  }
};

const saveUserSessionMode = async (userId, sessionLoginMode) => {
  const res = await fetch(`${API_BASE}/api/login/admin/users/${userId}/session-policy`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify({ sessionLoginMode }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    setError(data.error || "Session policy update failed");
    return;
  }

  await loadUserSessions();
  setMessage("Session policy updated");
};

const saveScannerTotpPolicy = async (userId, scannerTotpRequired) => {
  const res = await fetch(`${API_BASE}/api/login/admin/users/${userId}/scanner-totp-policy`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify({ scannerTotpRequired }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    setError(data.error || "Scanner TOTP policy update failed");
    return;
  }

  await loadUserSessions();
  setMessage("Scanner TOTP policy updated");
};

const revokeUserSessions = async userId => {
  const res = await fetch(`${API_BASE}/api/login/admin/users/${userId}/revoke-sessions`, {
    method: "POST",
    headers: authHeaders,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    setError(data.error || "Session revoke failed");
    return;
  }

  await loadUserSessions();
  setMessage("Sessions revoked");
};

const loadFaceLoginUsers = async () => {
  const res = await fetch(`${API_BASE}/api/face-login/admin/faces`, { headers: authHeaders });
  const data = await res.json().catch(() => ({}));
  if (res.ok && data.success) setFaceLoginUsers(data.faces || []);
};

const deleteFaceLogin = async userId => {
  const res = await fetch(`${API_BASE}/api/face-login/admin/faces/${userId}`, {
    method: "DELETE",
    headers: authHeaders,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    setError(data.error || "Face delete failed");
    return;
  }
  await loadFaceLoginUsers();
  setMessage("Face login deleted");
};

const revokeFaceLoginSessions = async userId => {
  const res = await fetch(`${API_BASE}/api/face-login/admin/faces/${userId}/revoke-sessions`, {
    method: "POST",
    headers: authHeaders,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    setError(data.error || "Face session revoke failed");
    return;
  }
  await loadUserSessions();
  setMessage("Face login sessions revoked");
};

const toggleRolePage = (role, path) => {
  setRolePageAccess(prev => {
    const current = prev[role] || [];
    const next = current.includes(path)
      ? current.filter(item => item !== path)
      : [...current, path];

    return { ...prev, [role]: next };
  });
};

const updateSubscription = async (item, status) => {
  setMessage("");
  setError("");

  const res = await fetch(`${API_BASE}/api/subscription/users/${item.id}/status`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify({ status }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.success) {
    setError(data.error || "Subscription update failed");
    return;
  }

  setMessage(status === "paid" ? "Subscription marked paid" : "Subscription marked pending");
  await loadUsers();
};

const getUserEdit = (item, key) => {
  return userEdits[item.id]?.[key] ?? item[key] ?? "";
};

const updateUserEdit = (item, key, value) => {
  setUserEdits(prev => ({
    ...prev,
    [item.id]: {
      ...(prev[item.id] || {}),
      [key]: value,
    },
  }));
};

const saveUserDetails = async (item) => {
  setMessage("");
  setError("");

  const body = {
    fullName: getUserEdit(item, "fullName"),
    email: getUserEdit(item, "email"),
    phone: getUserEdit(item, "phone"),
  };

  const res = await fetch(`${API_BASE}/api/login/users/${item.id}`, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.user) {
    setError(data.error || "User details save failed");
    return;
  }

  setMessage("User details saved");
  setSelectedUser(null);
  await loadUsers();
};

  const loadUsers = async () => {
    const res = await fetch(`${API_BASE}/api/login/users`, {
      headers: authHeaders,
    });

    const data = await res.json().catch(() => ({}));
    if (res.status === 401) {
  logoutToLogin();
  return;
}


    if (!res.ok) {
      throw new Error(data.error || "Failed to load users");
    }

    setUsers(data.users || []);
  };

  useEffect(() => {
    if (!token || currentUser?.role !== "admin") {
      navigate("/login", { replace: true });
      return;
    }

    loadSecretSettings().catch(() => {});
    loadRolePageAccess().catch(() => {});
    loadUsers().catch(err => setError(err.message));
    loadPaymentDetails().catch(() => {});
  }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/login/users`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(form),
      });

      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
  logoutToLogin();
  return;
}


      if (!res.ok || !data.user) {
        throw new Error(data.error || "User create failed");
      }

      setForm({ username: "", password: "", role: "user", fullName: "", email: "", phone: "" });
      setMessage("User account created");
      setShowCreateAccount(false);
      await loadUsers();
    } catch (err) {
      setError(err.message || "User create failed");
    }
  };

  const toggleActive = async (item) => {
    if (isFirstAdmin(item)) return;

    setMessage("");
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/login/users/${item.id}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ active: !item.active }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.user) {
        throw new Error(data.error || "User update failed");
      }

      await loadUsers();
    } catch (err) {
      setError(err.message || "User update failed");
    }
  };

  const deleteUser = async (item) => {
    if (isFirstAdmin(item)) return;

    if (!window.confirm(`Delete account "${item.username}"?`)) return;

    setMessage("");
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/login/users/${item.id}`, {
        method: "DELETE",
        headers: authHeaders,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "User delete failed");
      }

      setMessage("User account deleted");
      await loadUsers();
    } catch (err) {
      setError(err.message || "User delete failed");
    }
  };

  // Reset Function

  const resetTwoFactor = async (item) => {
  if (isFirstAdmin(item)) return;

  if (!window.confirm(`Reset Google Authenticator for "${item.username}"?`)) return;

  setMessage("");
  setError("");

  try {
    const res = await fetch(`${API_BASE}/api/2fa/users/${item.id}/reset`, {
      method: "POST",
      headers: authHeaders,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      throw new Error(data.error || "2FA reset failed");
    }

    setMessage("Two factor setup reset. User will set it up again on next login.");
    await loadUsers();
  } catch (err) {
    setError(err.message || "2FA reset failed");
  }
};


  return (
      <div className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-gray-400 mt-1">Create login accounts and manage access.</p>
          </div>

                    <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setAdminInnerTab("dashboard")}
              className={`rounded px-4 py-2 font-bold ${adminInnerTab === "dashboard" ? "terminal-tab-active" : ""}`}
            >
              Dashboard
            </button>

            <button
              type="button"
              onClick={() => setAdminInnerTab("bloomberg-settings")}
              className={`rounded px-4 py-2 font-bold ${adminInnerTab === "bloomberg-settings" ? "terminal-tab-active" : ""}`}
            >
              Bloomberg Settings
            </button>

            <button
              type="button"
              onClick={() => setAdminInnerTab("secret-page-settings")}
              className={`rounded px-4 py-2 font-bold ${adminInnerTab === "secret-page-settings" ? "terminal-tab-active" : ""}`}
            >
              Secret Page Settings
            </button>

            <button
              type="button"
              onClick={() => navigate("/retention-days")}
              className="rounded px-4 py-2 font-bold"
            >
              Retention Days
            </button>

            <button
              type="button"
              onClick={() => setAdminInnerTab("dhan-token-manager")}
              className={`rounded px-4 py-2 font-bold ${adminInnerTab === "dhan-token-manager" ? "terminal-tab-active" : ""}`}
            >
              Dhan Token Manager
            </button>

          </div>

              <div className="bg-blue-500/15 text-blue-300 border border-blue-500/30 px-4 py-2 rounded-lg flex items-center gap-2">
                <Shield size={18} /> Admin Access
              </div>
            </div>
          </div>
              {adminInnerTab === "dhan-token-manager" && (
                <DhanTokenManager />
              )}

              {adminInnerTab === "bloomberg-settings" && (
          <div className="bg-[#1e293b] rounded-xl p-5 mb-6">
            <h2 className="text-xl font-semibold mb-4">Bloomberg Settings</h2>

            <details key={`expiry-decay-${adminDropdownResetKey}`} className="rounded-lg border border-gray-700 bg-[#0f172a] p-4 mb-4">
              <summary className="cursor-pointer text-lg font-bold">
                Expiry Decay Desk
              </summary>

              <div className="mt-4 space-y-4">
                {[
                  {
                    title: "NIFTY Expiry",
                    minKey: "optionDecayNiftyPremiumMin",
                    maxKey: "optionDecayNiftyPremiumMax",
                  },
                  {
                    title: "SENSEX Expiry",
                    minKey: "optionDecaySensexPremiumMin",
                    maxKey: "optionDecaySensexPremiumMax",
                  },
                  {
                    title: "BANKNIFTY Expiry",
                    minKey: "optionDecayBankNiftyPremiumMin",
                    maxKey: "optionDecayBankNiftyPremiumMax",
                  },
                ].map(item => (
                  <div key={item.title} className="rounded border p-4" style={{ borderColor: "rgba(247,201,72,0.28)" }}>
                    <div className="mb-3 font-bold">{item.title}</div>

                    <div className="grid md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Premium From</label>
                        <input
                          type="number"
                          min="1"
                          value={secretSettings[item.minKey] ?? 5}
                          onChange={e =>
                            setSecretSettings(prev => ({
                              ...prev,
                              [item.minKey]: e.target.value,
                            }))
                          }
                          className="p-3 rounded bg-gray-700 border border-gray-600 w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2">Premium To</label>
                        <input
                          type="number"
                          min="1"
                          value={secretSettings[item.maxKey] ?? 150}
                          onChange={e =>
                            setSecretSettings(prev => ({
                              ...prev,
                              [item.maxKey]: e.target.value,
                            }))
                          }
                          className="p-3 rounded bg-gray-700 border border-gray-600 w-full"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={saveExpiryDecaySettings}
                        className="btn-solid-green rounded px-4 py-3 font-semibold"
                      >
                        Save {item.title}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </details>

                <details
                  key={`secret-expiry-refresh-${adminDropdownResetKey}`}
                  className="rounded-lg border border-gray-700 bg-[#0f172a] p-4 mb-4"
                >
                  <summary className="cursor-pointer text-lg font-bold">
                    Expiry Decay Desk Auto Refresh
                  </summary>

                  <div className="mt-4 grid md:grid-cols-[260px_auto_1fr] gap-4 items-end">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Refresh Seconds</label>
                      <input
                        type="number"
                        min="1"
                        max="300"
                        value={secretSettings.secretExpiryDeskRefreshSeconds ?? 5}
                        onChange={e =>
                          setSecretSettings(prev => ({
                            ...prev,
                            secretExpiryDeskRefreshSeconds: e.target.value,
                          }))
                        }
                        className="p-3 rounded bg-gray-700 border border-gray-600 w-full"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={saveSecretExpiryDeskRefreshSettings}
                      className="btn-solid-green rounded px-4 py-3 font-semibold"
                    >
                      Save Refresh
                    </button>

                    <div className="text-sm text-gray-400">
                      Applies to NIFTY, SENSEX, BANKNIFTY, BANKEX, and JAMMU tabs. Expiry Decay tab keeps its own refresh button.
                    </div>
                  </div>
                </details>

            <details className="rounded-lg border border-gray-700 bg-[#0f172a] p-4 mb-4">
              <summary className="cursor-pointer text-lg font-bold">
                Manual Support Range Settings
              </summary>

              <div className="mt-4 flex flex-wrap gap-2">
                {BLOOMBERG_MANUAL_SUPPORT_TABS.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setManualSupportTab(tab);

                      const cachedRows = readManualSupportRowsCache();
                      setManualSupportRows(cachedRows[tab.id] || []);
                    }}
                    className={`rounded px-4 py-2 font-bold ${manualSupportTab.id === tab.id ? "terminal-tab-active" : ""}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <input
                  value={manualSupportSearch}
                  onChange={e => setManualSupportSearch(e.target.value)}
                  placeholder="Search strike"
                  className="rounded border px-4 py-3"
                  style={{ background: "#020202" }}
                />

                <button
                  type="button"
                  onClick={loadManualSupportRows}
                  className="btn-solid-green rounded px-4 py-3 font-semibold"
                >
                  {manualSupportLoading ? "Loading..." : "Fetch Live Strikes"}
                </button>

                <button
                  type="button"
                  onClick={manualSupportFetchStarted ? stopManualSupportFetch : startManualSupportFetch}
                  className={`rounded px-4 py-3 font-semibold ${manualSupportFetchStarted ? "btn-maroon-red" : "btn-solid-green"}`}
                >
                  {manualSupportFetchStarted ? "Stop" : "Start"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const next = !manualSupportAutoRefresh;

                    localStorage.setItem(MANUAL_SUPPORT_AUTO_REFRESH_KEY, next ? "1" : "0");
                    setManualSupportAutoRefresh(next);

                    if (next) {
                      startManualSupportFetch();
                    } else {
                      window.dispatchEvent(new Event("bloomberg-manual-support-fetch-control"));
                    }
                  }}
                  className={`rounded px-4 py-3 font-semibold ${manualSupportAutoRefresh ? "terminal-tab-active" : ""}`}
                  style={{
                    background: manualSupportAutoRefresh ? "linear-gradient(180deg, #006b49, #003f2f)" : "#020202",
                    border: manualSupportAutoRefresh ? "1px solid #22c55e" : "1px solid rgba(247,201,72,0.26)",
                    color: manualSupportAutoRefresh ? "#ffffff" : "#f7c948",
                    boxShadow: manualSupportAutoRefresh ? "0 0 16px rgba(0,107,73,0.75)" : "none",
                  }}
                >
                  Auto Refresh 1s
                </button>

                <button
                  type="button"
                  onClick={saveBloombergSettings}
                  className="btn-solid-green rounded px-4 py-3 font-semibold"
                >
                  Save Manual Ranges
                </button>
              </div>

              <div
                ref={manualTopScrollRef}
                onScroll={() => syncManualSupportScroll("top")}
                className="mt-4 w-full overflow-x-auto manual-support-scroll manual-support-scroll-top"
              >
                <div className="min-w-[1720px]" />
              </div>

              <div
                ref={manualTableScrollRef}
                onScroll={() => syncManualSupportScroll("table")}
                className="w-full overflow-x-auto pb-3 manual-support-scroll"
                style={{ scrollbarGutter: "stable" }}
              >
                <table className="w-full min-w-[1720px] text-sm">
                  <thead>
                    <tr>
                      <th className="px-3 py-3 text-left">
                        <button
                          type="button"
                          onClick={() => toggleManualSupportSort("strike")}
                          className="font-bold"
                        >
                          Strike ⇅
                        </button>
                      </th>

                      <th className="px-3 py-3 text-left">
                        <button
                          type="button"
                          onClick={() => toggleManualSupportSort("entry")}
                          className="font-bold"
                        >
                          Entry ⇅
                        </button>
                      </th>

                      <th className="px-3 py-3 text-left">Support Range</th>
                      <th className="px-3 py-3 text-left">StopLoss</th>
                      <th className="px-3 py-3 text-left">Qty</th>
                      <th className="px-3 py-3 text-left">SL Points</th>
                      <th className="px-3 py-3 text-left">Candles Timing</th>
                    </tr>
                  </thead>

                  <tbody>
                    {sortedManualSupportRows.map(row => {
                        const key = manualSupportKey(manualSupportTab.chainKey, manualSupportTab.side, row.strike);
                        const saved = secretSettings.bloombergManualSupportRanges?.[key] || {};
                        const defaults = getManualSupportDefaults(manualSupportTab.chainKey);
                        const stopLossPrice = getManualStopLossPrice(row, saved, defaults);
                        const totalSlPoints = getManualTotalSlPoints(row, stopLossPrice);
                        const totalLoss = totalSlPoints * Number(saved.quantity || defaults.quantity || 0);

                        return (
                          <tr key={key}>
                            <td className="px-3 py-3 font-bold">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => updateManualSupportRange(row, "notifyEnabled", !saved.notifyEnabled)}
                                  className={`rounded px-2 py-2 ${saved.notifyEnabled ? "terminal-tab-active" : ""}`}
                                  title="Notify support and stoploss hits"
                                >
                                  <Bell size={16} />
                                </button>

                                <span>{manualSupportTab.side} {row.strike}</span>
                              </div>
                            </td>

                            <td className="px-3 py-3 font-bold">
                              Rs.{Number(row.ltp || 0).toFixed(2)}
                            </td>

                            <td className="px-3 py-3">
                              <div className="flex gap-2">
                                <input
                                  value={saved.supportLow || ""}
                                  onChange={e => updateManualSupportRange(row, "supportLow", e.target.value)}
                                  placeholder="75.50"
                                  className="w-32 rounded border px-3 py-2"
                                  style={{ background: "#020202" }}
                                />

                                <input
                                  value={saved.supportHigh || ""}
                                  onChange={e => updateManualSupportRange(row, "supportHigh", e.target.value)}
                                  placeholder="80.50"
                                  className="w-32 rounded border px-3 py-2"
                                  style={{ background: "#020202" }}
                                />
                              </div>
                            </td>

                            <td className="px-3 py-3">
                              <input
                                type="number"
                                min="0"
                                step="0.05"
                                value={stopLossPrice}
                                onChange={e => updateManualSupportRange(row, "stopLossPrice", e.target.value)}
                                placeholder="7.00"
                                className="w-24 rounded border px-3 py-2"
                                style={{ background: "#020202" }}
                              />

                              <div className="mt-1 text-xs" style={{ color: "#9f7a24" }}>
                                SL Pts: {totalSlPoints} | Loss: Rs.{Number(totalLoss || 0).toLocaleString("en-IN")}
                              </div>
                            </td>

                            <td className="px-3 py-3">
                              <input
                                type="number"
                                min="1"
                                value={saved.quantity || defaults.quantity}
                                onChange={e => updateManualSupportRange(row, "quantity", e.target.value)}
                                className="w-24 rounded border px-3 py-2"
                                style={{ background: "#020202" }}
                              />
                            </td>

                            <td className="px-3 py-3">
                              <input
                                type="number"
                                min="1"
                                value={saved.slPoints || defaults.slPoints}
                                onChange={e => updateManualSupportRange(row, "slPoints", e.target.value)}
                                className="w-24 rounded border px-3 py-2"
                                style={{ background: "#020202" }}
                              />
                            </td>

                            <td className="px-3 py-3">
                              <select
                                value={saved.candleMinutes || defaults.candleMinutes}
                                onChange={e => updateManualSupportRange(row, "candleMinutes", e.target.value)}
                                className="w-32 rounded border px-3 py-2"
                                style={{ background: "#020202" }}
                              >
                                <option value="1">1 Min</option>
                                <option value="5">5 Min</option>
                                <option value="15">15 Min</option>
                                <option value="60">60 Min</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </details>

            <details key={`bloomberg-refresh-${adminDropdownResetKey}`} className="rounded-lg border border-gray-700 bg-[#0f172a] p-4 mb-4">
              <summary className="cursor-pointer text-lg font-bold">
                Bloomberg Terminal Refresh Time
              </summary>

              <div className="mt-4 grid md:grid-cols-[260px_auto_1fr] gap-4 items-end">
                <div>
                  <label className="block text-sm font-semibold mb-2">Refresh Seconds</label>
                  <input
                    type="number"
                    min="1"
                    max="300"
                    value={secretSettings.bloombergRefreshSeconds ?? ""}
                    onChange={e =>
                      setSecretSettings(prev => ({
                        ...prev,
                        bloombergRefreshSeconds: e.target.value,
                      }))
                    }
                    className="p-3 rounded bg-gray-700 border border-gray-600 w-full"
                  />
                </div>

                <button
                  type="button"
                  onClick={saveSecretRefreshSettings}
                  className="btn-solid-green rounded px-4 py-3 font-semibold"
                >
                  Save Refresh Time
                </button>

                <div className="text-sm text-gray-400">
                  Applies only to Bloomberg Terminal tabs.
                </div>
              </div>
            </details>

            

            <div className="mt-4 text-sm text-gray-400">
              Risk and Loss are calculated automatically inside Bloomberg scanners from Entry, Qty, and SL points.
            </div>
          </div>
        )}


                    {adminInnerTab === "secret-page-settings" && (
          <div className="bg-[#1e293b] rounded-xl p-5 mb-6">
            <h2 className="text-xl font-semibold mb-4">Secret Page Settings</h2>

            <div className="bg-[#1e293b] rounded-xl p-5 mb-6">
              <button type="button" onClick={() => setShowSecretSettings(prev => !prev)} className="w-full flex items-center justify-between text-left">
                <h2 className="text-xl font-semibold flex items-center gap-2"><Shield size={18} /> Secret Page Credentials</h2>
                <span className="text-blue-300 flex items-center gap-2 text-sm">{showSecretSettings ? "Hide" : "Show"} {showSecretSettings ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</span>
              </button>
            
              {showSecretSettings && (
                <>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <input value={secretSettings.clientId || ""} onChange={e => setSecretSettings(prev => ({ ...prev, clientId: e.target.value }))} placeholder="Secret Page Client ID" className="p-3 rounded bg-gray-700 border border-gray-600" />
                    <input value={secretSettings.accessToken || ""} onChange={e => setSecretSettings(prev => ({ ...prev, accessToken: e.target.value }))} placeholder="Secret Page Access Token" className="p-3 rounded bg-gray-700 border border-gray-600" />
                  </div>
            
                  <button type="button" onClick={saveSecretSettings} className="mt-4 bg-blue-500 hover:bg-blue-400 rounded px-4 py-2 font-semibold flex items-center gap-2">
                    <Save size={16} /> Save Secret Settings
                  </button>
                </>
              )}
            </div>


          <div className="bg-[#1e293b] rounded-xl p-5 mb-6">
              <button
                type="button"
                onClick={() => setShowSecretRefresh(prev => !prev)}
                className="w-full flex items-center justify-between text-left"
              >
                <h2 className="text-xl font-semibold">Secret Page Refresh Time</h2>
                <span className="text-blue-300 text-sm">
                  {showSecretRefresh ? "Hide" : "Show"}
                </span>
              </button>

              {showSecretRefresh && (
                <div className="mt-4 flex flex-wrap items-end gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Refresh Seconds</label>
                    <input
                      type="number"
                      min="5"
                      max="300"
                      value={secretSettings.refreshSeconds ?? ""}
                      onChange={e => {
                        const value = e.target.value;
                        setSecretSettings(prev => ({
                          ...prev,
                          refreshSeconds: value,
                        }));
                      }}
                      className="p-3 rounded bg-gray-700 border border-gray-600 w-48"
                    />
                  </div>
            
                  <button
                    type="button"
                    onClick={saveSecretRefreshSettings}
                    className="bg-blue-500 hover:bg-blue-400 rounded px-4 py-3 font-semibold"
                  >
                    Save Refresh Time
                  </button>
            
                  <div className="text-sm text-gray-400">
                    Applies only to Secret page tabs. Parent order alignment remains 5 seconds.
                  </div>
                </div>
              )}
            </div>

          <div className="bg-[#1e293b] rounded-xl p-5 mb-6">
                    <button
                      type="button"
                      onClick={() => setShowSupportRefresh(prev => !prev)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <h2 className="text-xl font-semibold">Support Scanner Refresh Time</h2>
                      <span className="text-blue-300 text-sm">
                        {showSupportRefresh ? "Hide" : "Show"}
                      </span>
                    </button>

                    {showSupportRefresh && (
                      <div className="mt-4 flex flex-wrap items-end gap-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2">Refresh Seconds</label>
                          <input
                            type="number"
                            min="5"
                            max="300"
                            value={secretSettings.supportRefreshSeconds ?? ""}
                            onChange={e =>
                              setSecretSettings(prev => ({
                                ...prev,
                                supportRefreshSeconds: e.target.value,
                              }))
                            }
                            className="p-3 rounded bg-gray-700 border border-gray-600 w-48"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={saveSecretRefreshSettings}
                          className="bg-blue-500 hover:bg-blue-400 rounded px-4 py-3 font-semibold"
                        >
                          Save Support Refresh Time
                        </button>

                        <div className="text-sm text-gray-400">
                          Applies only to Support Scanner auto refresh. Candle interval remains 1 Min / 5 Min.
                        </div>
                      </div>
                    )}
                  </div>

            <details key={`secret-expiry-decay-${adminDropdownResetKey}`} className="rounded-lg border border-gray-700 bg-[#0f172a] p-4 mb-4">
              <summary className="cursor-pointer text-lg font-bold">
                Dhan Expiry Decay Desk
              </summary>

              <div className="mt-4 space-y-4">
                {[
                  { title: "NIFTY Expiry", minKey: "secretOptionDecayNiftyPremiumMin", maxKey: "secretOptionDecayNiftyPremiumMax" },
                  { title: "SENSEX Expiry", minKey: "secretOptionDecaySensexPremiumMin", maxKey: "secretOptionDecaySensexPremiumMax" },
                  { title: "BANKNIFTY Expiry", minKey: "secretOptionDecayBankNiftyPremiumMin", maxKey: "secretOptionDecayBankNiftyPremiumMax" },
                ].map(item => (
                  <div key={item.title} className="rounded border p-4" style={{ borderColor: "rgba(247,201,72,0.28)" }}>
                    <div className="mb-3 font-bold">{item.title}</div>

                    <div className="grid md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Premium From</label>
                        <input
                          type="number"
                          min="1"
                          value={secretSettings[item.minKey] ?? 5}
                          onChange={e => setSecretSettings(prev => ({ ...prev, [item.minKey]: e.target.value }))}
                          className="p-3 rounded bg-gray-700 border border-gray-600 w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2">Premium To</label>
                        <input
                          type="number"
                          min="1"
                          value={secretSettings[item.maxKey] ?? 150}
                          onChange={e => setSecretSettings(prev => ({ ...prev, [item.maxKey]: e.target.value }))}
                          className="p-3 rounded bg-gray-700 border border-gray-600 w-full"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={saveSecretPageExpiryDecaySettings}
                        className="btn-solid-green rounded px-4 py-3 font-semibold"
                      >
                        Save {item.title}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

                  {adminInnerTab === "dashboard" && (
  <>

        <div className="bg-[#1e293b] rounded-xl p-5 mb-6">
    <button
      type="button"
      onClick={() => setShowPaymentDetails(prev => !prev)}
      className="w-full flex items-center justify-between text-left"
    >
      <h2 className="text-xl font-semibold flex items-center gap-2">
        <CreditCard size={18} /> NEXO Payment Details
      </h2>
  
      <span className="text-blue-300 flex items-center gap-2 text-sm">
        {showPaymentDetails ? "Hide" : "Show"}
        {showPaymentDetails ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </span>
    </button>
  
    {showPaymentDetails && (
      <>
  
    <div className="grid md:grid-cols-3 gap-4">
      <input
        value={paymentDetails.upiId || ""}
        onChange={(e) => setPaymentDetails(prev => ({ ...prev, upiId: e.target.value }))}
        placeholder="UPI ID"
        className="p-3 rounded bg-gray-700 border border-gray-600"
      />
  
      <input
        value={paymentDetails.qrImageUrl || ""}
        onChange={(e) => setPaymentDetails(prev => ({ ...prev, qrImageUrl: e.target.value }))}
        placeholder="QR image URL"
        className="p-3 rounded bg-gray-700 border border-gray-600"
      />
  
      <input
        value={paymentDetails.accountName || ""}
        onChange={(e) => setPaymentDetails(prev => ({ ...prev, accountName: e.target.value }))}
        placeholder="Account name"
        className="p-3 rounded bg-gray-700 border border-gray-600"
      />
  
      <input
        value={paymentDetails.bankName || ""}
        onChange={(e) => setPaymentDetails(prev => ({ ...prev, bankName: e.target.value }))}
        placeholder="Bank name"
        className="p-3 rounded bg-gray-700 border border-gray-600"
      />
  
      <input
        value={paymentDetails.accountNumber || ""}
        onChange={(e) => setPaymentDetails(prev => ({ ...prev, accountNumber: e.target.value }))}
        placeholder="Account number"
        className="p-3 rounded bg-gray-700 border border-gray-600"
      />
  
      <input
        value={paymentDetails.ifsc || ""}
        onChange={(e) => setPaymentDetails(prev => ({ ...prev, ifsc: e.target.value }))}
        placeholder="IFSC"
        className="p-3 rounded bg-gray-700 border border-gray-600"
      />
    </div>
  
    <textarea
      value={paymentDetails.instructions || ""}
      onChange={(e) => setPaymentDetails(prev => ({ ...prev, instructions: e.target.value }))}
      placeholder="Payment instructions"
      className="w-full p-3 rounded bg-gray-700 border border-gray-600 mt-4"
    />
  
    <button
      type="button"
      onClick={savePaymentDetails}
      className="mt-4 bg-blue-500 hover:bg-blue-400 rounded px-4 py-2 font-semibold flex items-center gap-2"
    >
      <Save size={16} /> Save Payment Details
    </button>
      </>
    )}
  </div>
  
  <div className="bg-[#1e293b] rounded-xl p-5 mb-6">
    <button type="button" onClick={() => setShowRoleAccess(prev => !prev)} className="w-full flex items-center justify-between text-left">
      <h2 className="text-xl font-semibold flex items-center gap-2"><Users size={18} /> User Roles Page Restrict</h2>
      <span className="text-blue-300 flex items-center gap-2 text-sm">{showRoleAccess ? "Hide" : "Show"} {showRoleAccess ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</span>
    </button>

    {showRoleAccess && (
      <>
        <div className="grid md:grid-cols-4 gap-4 mt-4">
          {ROLE_OPTIONS.map(role => (
            <div key={role} className="rounded bg-[#0f172a] border border-gray-700 p-4">
              <div className="font-bold uppercase mb-3 text-blue-300">{role}</div>
  
              <div className="space-y-2">
                {PAGE_OPTIONS
                  .filter(page => role === "admin" || page.path !== "/admin-dashboard")
                  .map(page => (
                    <label key={page.path} className="flex items-center gap-2 text-sm text-gray-200">
                      <input
                        type="checkbox"
                        checked={
                          page.path === "/admin-dashboard" && role === "admin"
                            ? true
                            : (rolePageAccess[role] || []).includes(page.path)
                        }
                        disabled={page.path === "/admin-dashboard" && role === "admin"}
                        onChange={() => toggleRolePage(role, page.path)}
                      />
                      {page.label}
                    </label>
                  ))}
              </div>
            </div>
          ))}
        </div>
  
        <button type="button" onClick={saveRolePageAccess} className="mt-4 bg-blue-500 hover:bg-blue-400 rounded px-4 py-2 font-semibold flex items-center gap-2">
          <Save size={16} /> Save Role Access
        </button>
      </>
    )}
  </div>

      <div className="bg-[#1e293b] rounded-xl p-5 mb-6 w-full">
  <button
    type="button"
    onClick={() => {
      setShowUserSessions(prev => !prev);
      loadUserSessions();
    }}
    className="w-full flex items-center justify-between text-left"
  >
    <h2 className="text-xl font-semibold flex items-center gap-2">
      <Users size={18} /> User Active Sessions
    </h2>
    <span className="text-blue-300 text-sm">
      {showUserSessions ? "Hide" : "Show"}
    </span>
  </button>

    {showUserSessions && (
  <>
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <button
        type="button"
        onClick={loadUserSessions}
        className="btn-solid-green rounded px-4 py-3 font-semibold"
      >
        Detect Active Sessions
      </button>

      <input
        value={sessionSearchText}
        onChange={e => setSessionSearchText(e.target.value)}
        placeholder="Search username, role, user ID"
        className="w-full rounded border px-4 py-3 md:w-[540px]"
        style={{ background: "#020202" }}
      />
    </div>

    <div className="mt-4 space-y-3">
      {filteredSessionUsers.map(item => (
        <div key={item.id} className="rounded border border-gray-700 bg-[#0f172a] p-4">
          <div className="grid gap-4 items-center xl:grid-cols-[1.2fr_0.6fr_1.1fr_0.8fr_1.1fr_auto]">
            <div>
              <div className="font-bold">{item.username}</div>
              <div className="text-sm text-gray-400">User ID: {item.id}</div>
            </div>

            <div>{item.role}</div>

            <select
              value={item.sessionLoginMode || "window"}
              onChange={e => saveUserSessionMode(item.id, e.target.value)}
              className="p-3 rounded bg-gray-700 border border-gray-600"
            >
              <option value="window">Window Login</option>
              <option value="window_mobile">Both Window & Mobile</option>
              <option value="no_limit">No Limit</option>
            </select>

          <select
            value={item.scannerTotpRequired === false ? "no" : "yes"}
            onChange={e => saveScannerTotpPolicy(item.id, e.target.value === "yes")}
            className="p-3 rounded bg-gray-700 border border-gray-600"
            title="Only for scanner login. Username password login always asks TOTP."
          >
            <option value="yes">TOTP: Yes</option>
            <option value="no">TOTP: No</option>
          </select>

            <div className="text-sm">
              <div className="font-bold mb-1">
                Login Devices: {(item.activeSessions || []).length}
              </div>

              {(item.activeSessions || []).length ? (
                (item.activeSessions || []).map(session => (
                  <div key={session.sessionId} className="mb-2 text-gray-300">
                    <div>{session.loginLabel || session.deviceLabel || session.deviceType}</div>
                    <div>IP: {cleanDisplayIp(session.ip)}</div>
                    <div>Last: {session.lastSeenAt ? new Date(session.lastSeenAt).toLocaleString() : "-"}</div>
                  </div>
                ))
              ) : (
                <div className="text-gray-500">No active session</div>
              )}
            </div>

            <button
              type="button"
              onClick={() => revokeUserSessions(item.id)}
              className="btn-maroon-red rounded px-4 py-2 font-semibold"
            >
              Revoke Session
            </button>
          </div>
        </div>
      ))}
        </div>
  </>
)}
</div> 

        <div className="bg-[#1e293b] rounded-xl p-5 mb-6">
          <button
            type="button"
            onClick={() => {
              setShowFaceLoginSettings(prev => !prev);
              loadFaceLoginUsers();
            }}
            className="w-full flex items-center justify-between text-left"
          >
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Shield size={18} /> Face Login Settings
            </h2>
            <span className="text-blue-300 text-sm">{showFaceLoginSettings ? "Hide" : "Show"}</span>
          </button>

          {showFaceLoginSettings && (
            <div className="mt-4 grid gap-3">
              {faceLoginUsers.map(item => (
                <div key={item.user.id} className="rounded border border-gray-700 bg-[#0f172a] p-4 grid md:grid-cols-[120px_1fr_auto] gap-4 items-center">
                  {item.face?.image ? <img src={item.face.image} alt={item.user.username} className="w-[120px] h-[82px] object-cover rounded" /> : <div className="text-gray-500">No image</div>}
                  <div>
                    <div className="font-bold">{item.user.username}</div>
                    <div className="text-sm text-gray-400">{item.user.role}</div>
                    <div className="text-xs text-gray-500">Saved: {item.face?.updatedAt ? new Date(item.face.updatedAt).toLocaleString() : "-"}</div>
                    <div className="text-xs text-gray-500">Last Face Login: {item.face?.lastLoginAt ? new Date(item.face.lastLoginAt).toLocaleString() : "-"}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button type="button" onClick={() => revokeFaceLoginSessions(item.user.id)} className="btn-maroon-red rounded px-4 py-2 text-sm font-semibold">Revoke Session</button>
                    <button type="button" onClick={() => deleteFaceLogin(item.user.id)} className="bg-red-700 rounded px-4 py-2 text-sm font-semibold">Delete Face</button>
                  </div>
                </div>
              ))}
              {!faceLoginUsers.length && <div className="text-gray-500">No saved face logins.</div>}
            </div>
          )}
        </div>


        <div className="grid lg:grid-cols-[420px_1fr] gap-6">
          <form onSubmit={createUser} className="bg-[#1e293b] rounded-xl p-5 h-fit">
            <button
              type="button"
              onClick={() => setShowCreateAccount(prev => !prev)}
              className="w-full flex items-center justify-between mb-4 text-left"
            >
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Plus size={18} /> Create Account
              </h2>
              <span className="text-blue-300 text-sm">
                {showCreateAccount ? "Hide" : "Show"}
              </span>
            </button>
  
            {showCreateAccount && (
    <>
            
            <label className="block text-sm font-semibold mb-2">Username</label>
            <input
              value={form.username}
              onChange={(e) => setForm(prev => ({ ...prev, username: e.target.value }))}
              className="w-full p-3 rounded bg-gray-700 border border-gray-600 mb-4"
              placeholder="Enter username"
            />
  
            <label className="block text-sm font-semibold mb-2">Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
              className="w-full p-3 rounded bg-gray-700 border border-gray-600 mb-4"
              placeholder="Enter password"
            />
  
            <label className="block text-sm font-semibold mb-2">Full Name</label>
            <input
              value={form.fullName}
              onChange={(e) => setForm(prev => ({ ...prev, fullName: e.target.value }))}
              className="w-full p-3 rounded bg-gray-700 border border-gray-600 mb-4"
              placeholder="Enter full name"
            />
  
            <label className="block text-sm font-semibold mb-2">Business Email</label>
            <input
              value={form.email}
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full p-3 rounded bg-gray-700 border border-gray-600 mb-4"
              placeholder="Enter business email"
            />
  
            <label className="block text-sm font-semibold mb-2">Phone</label>
            <input
              value={form.phone}
              onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full p-3 rounded bg-gray-700 border border-gray-600 mb-4"
              placeholder="Enter phone number"
            />
  
            <label className="block text-sm font-semibold mb-2">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value }))}
              className="w-full p-3 rounded bg-gray-700 border border-gray-600 mb-5"
            >
              <option value="user">User Account</option>
              <option value="nexo">Nexo Account</option>
              <option value="secret">Secret Account</option>
              <option value="admin">Admin Account</option>
            </select>
  
            {message && <div className="text-green-300 text-sm mb-3">{message}</div>}
            {error && <div className="text-red-300 text-sm mb-3">{error}</div>}
  
            <button className="w-full bg-blue-500 hover:bg-blue-400 rounded py-3 font-semibold">
              Create Account
            </button>
              </>
  )}
          </form>
  
          <div className="bg-[#1e293b] rounded-xl overflow-hidden">
            <div className="p-5 border-b border-gray-700 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setShowLoginAccounts(prev => !prev)}
                className="flex items-center gap-2 hover:text-blue-300"
              >
                <Users size={18} /> Login Accounts
                <span className="text-blue-300 text-sm">
                  {showLoginAccounts ? "Hide" : "Show"}
                </span>
              </button>
  
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search username, name, phone, email"
                className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm w-[320px] outline-none focus:border-blue-400"
              />
            </div>
  
          {showLoginAccounts && (
<>

          <div className="grid grid-cols-7 p-4 text-gray-400 border-b border-gray-700 text-sm font-semibold uppercase">
            <span>Username</span>
            <span>Role</span>
            <span>Status</span>
            <span>Created</span>
            <span>Action</span>
            <span>Control</span>
            <span>Subscription</span>
          </div>

         {filteredUsers.map(item => (
  <React.Fragment key={item.id}>
    <div className="grid grid-cols-7 p-4 border-b border-gray-800 items-center">
      <button
        type="button"
        onClick={() => setSelectedUser(selectedUser?.id === item.id ? null : item)}
        className="flex items-center gap-2 text-left hover:text-blue-300"
      >
        <User size={16} /> {item.username}
        {item.role === "nexo" && (
          item.subscription?.status === "paid" ? (
            <CheckCircle size={15} className="text-green-300" />
          ) : (
            <Clock size={15} className="text-yellow-300" />
          )
        )}
        </button>

      <span className={item.role === "admin" ? "text-blue-300" : "text-gray-300"}>
        {item.role}
      </span>

      <span className={item.active ? "text-green-300" : "text-red-300"}>
        {item.active ? "Active" : "Disabled"}
      </span>

      <span className="text-gray-400 text-sm">
        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}
      </span>

      <button
        onClick={() => toggleActive(item)}
        disabled={isFirstAdmin(item)}
        className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded px-3 py-2 text-sm w-fit"
      >
        {isFirstAdmin(item) ? "Protected" : item.active ? "Disable" : "Enable"}
      </button>

      <div className="flex items-center gap-3">
        <button
          onClick={() => resetTwoFactor(item)}
          disabled={isFirstAdmin(item)}
          className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 disabled:opacity-40 rounded px-3 py-2 text-sm"
          title={isFirstAdmin(item) ? "First admin protected" : "Reset Google Authenticator"}
        >
          Reset 2FA
        </button>

        <button
          onClick={() => deleteUser(item)}
          disabled={isFirstAdmin(item)}
          className="text-red-400 hover:text-red-300 disabled:opacity-40 w-fit"
          title={isFirstAdmin(item) ? "First admin cannot be deleted" : "Delete account"}
        >
          <Trash2 size={18} />
        </button>
      </div>

      <div className="flex flex-col gap-2">
        {item.role === "nexo" ? (
          <>
            <div>
              {item.subscription?.status === "paid" ? (
                <span className="text-green-300 flex items-center gap-1">
                  <CheckCircle size={15} /> Paid
                </span>
              ) : (
                <span className="text-yellow-300 flex items-center gap-1">
                  <Clock size={15} /> Pending
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => updateSubscription(item, "paid")}
                className="bg-green-500/20 text-green-300 hover:bg-green-500/30 rounded px-2 py-1 text-xs"
              >
                Paid
              </button>

              <button
                onClick={() => updateSubscription(item, "pending")}
                className="bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30 rounded px-2 py-1 text-xs"
              >
                Pending
              </button>
            </div>
          </>
        ) : (
          <span className="text-gray-500">-</span>
        )}
      </div>
    </div>

    {selectedUser?.id === item.id && (
      <div className="grid grid-cols-2 gap-4 px-4 py-4 bg-[#0f172a] border-b border-gray-800 text-sm">
        
        <div>
          <div className="text-gray-500 uppercase text-xs mb-1">Full Name</div>
          <input
            value={getUserEdit(item, "fullName")}
            onChange={(e) => updateUserEdit(item, "fullName", e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
          />
        </div>

        <div>
          <div className="text-gray-500 uppercase text-xs mb-1">Business Email</div>
          <input
            value={getUserEdit(item, "email")}
            onChange={(e) => updateUserEdit(item, "email", e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
          />
        </div>

        <div>
          <div className="text-gray-500 uppercase text-xs mb-1">Phone</div>
          <input
            value={getUserEdit(item, "phone")}
            onChange={(e) => updateUserEdit(item, "phone", e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
          />
        </div>

        <div className="flex items-end">
          <button
            onClick={() => saveUserDetails(item)}
            className="bg-blue-500 hover:bg-blue-400 rounded px-4 py-2"
          >
            Save Details
          </button>
        </div>

        <div>
          <div className="text-gray-500 uppercase text-xs mb-1">Two Factor</div>
          <div className={item.twoFactorEnabled ? "text-green-300" : "text-yellow-300"}>
            {item.twoFactorEnabled ? "Enabled" : "Not setup"}
          </div>
        </div>

        <div>
          <div className="text-gray-500 uppercase text-xs mb-1">2FA Setup Date</div>
          <div>
            {item.twoFactorSetupAt
              ? new Date(item.twoFactorSetupAt).toLocaleString()
              : "-"}
          </div>
        </div>

        {item.role === "nexo" && (
          <div className="col-span-2 border-t border-gray-800 pt-4">
            <div className="text-gray-500 uppercase text-xs mb-2">NEXO Subscription</div>

            <div className="flex items-center gap-3 mb-3">
              {item.subscription?.status === "paid" ? (
                <span className="text-green-300 flex items-center gap-1">
                  <CheckCircle size={16} /> Paid
                </span>
              ) : (
                <span className="text-yellow-300 flex items-center gap-1">
                  <Clock size={16} /> Pending Payment
                </span>
              )}

              {item.subscription?.nextDueAt && (
                <span className="text-gray-400">
                  Renewal: {new Date(item.subscription.nextDueAt).toLocaleDateString()}
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => updateSubscription(item, "paid")}
                className="bg-green-500/20 text-green-300 hover:bg-green-500/30 rounded px-3 py-2 text-sm"
              >
                Mark Paid
              </button>

              <button
                onClick={() => updateSubscription(item, "pending")}
                className="bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30 rounded px-3 py-2 text-sm"
              >
                Mark Pending
              </button>
            </div>
          </div>
        )}
      </div>
    )}
  </React.Fragment>
))}
  
 {filteredUsers.length === 0 && (
  <div className="p-8 text-center text-gray-500">
    {searchText ? "No matching users found." : "No users found."}
  </div>
)}

  </>
)}

                </div>
      </div>
          </>
        )}
    </div>
  );
}
